import {
  Ticket,
  RefreshCw,
  GitPullRequest,
  GitMerge,
  Tag,
  CircleDot,
  MessageSquare,
  XCircle,
  AlertTriangle,
  Hammer,
  Rocket,
  CalendarClock,
  MousePointerClick,
  Clock,
  Inbox,
  Webhook,
  Wand2,
  HelpCircle,
  FolderGit2,
  GitBranch,
  ShieldCheck,
  Bot,
  BadgeCheck,
  Gauge,
  Sparkles,
  Network,
  FileSearch,
  ClipboardList,
  ScrollText,
  ShieldAlert,
  UserCheck,
  DollarSign,
  GitCompare,
  FileCheck2,
  MessageSquareText,
  Mail,
  BookOpen,
  StickyNote,
  LineChart,
  Send,
  Save,
  Bell,
  Slack,
} from "lucide-react";
import type { WfNode, WfNodeDef, WfNodeKind, WfEdge, WfEdgeTone } from "@/types";
import { uid } from "@/lib/utils";

/* Integration / surface metadata used for chips, glyphs and tinting. */
export const INTEGRATION_META: Record<
  string,
  { label: string; color: string; glyph: string }
> = {
  jira: { label: "Jira", color: "#2684ff", glyph: "J" },
  github: { label: "GitHub", color: "#8b5cf6", glyph: "" },
  slack: { label: "Slack", color: "#e01e5a", glyph: "#" },
  ci: { label: "CI", color: "#f59e0b", glyph: "CI" },
  gcal: { label: "Calendar", color: "#4285f4", glyph: "" },
  email: { label: "Email", color: "#ea4335", glyph: "@" },
  confluence: { label: "Confluence", color: "#1868db", glyph: "C" },
  notion: { label: "Notion", color: "#9ca3af", glyph: "N" },
  linear: { label: "Linear", color: "#5e6ad2", glyph: "L" },
  webhook: { label: "Webhook", color: "#6366f1", glyph: "{}" },
  monitor: { label: "Monitoring", color: "#ef4444", glyph: "▲" },
  deploy: { label: "Deploy", color: "#0ea5e9", glyph: "" },
  artifact: { label: "Artifact", color: "#64748b", glyph: "" },
  ares: { label: "ARES", color: "#f59e0b", glyph: "" },
  athena: { label: "ATHENA", color: "#3b82f6", glyph: "" },
  garlic: { label: "GARLIC", color: "#a855f7", glyph: "" },
  byonc: { label: "BYONC", color: "#10b981", glyph: "" },
  verification: { label: "Verification", color: "#22c55e", glyph: "" },
  workspace: { label: "Workspace", color: "#14b8a6", glyph: "" },
  hive: { label: "Hive", color: "#f5a623", glyph: "" },
};

export const KIND_META: Record<
  WfNodeKind,
  { label: string; color: string; verb: string }
> = {
  hook: { label: "Hook", color: "#f43f5e", verb: "Starts the workflow" },
  process: { label: "Process", color: "#3b82f6", verb: "Transforms work" },
  publish: { label: "Publish", color: "#10b981", verb: "Outputs a result" },
};

/* ── Node catalog ─────────────────────────────────────────────────────────── */

export const NODE_DEFS: WfNodeDef[] = [
  /* HOOKS (25) */
  { key: "hook.jira.created", kind: "hook", label: "Jira Ticket Created", group: "Project Management", icon: Ticket, integration: "jira", risk: "low", description: "Fires when a new Jira issue is created in a tracked project.", reads: ["jira:issues"] },
  { key: "hook.jira.updated", kind: "hook", label: "Jira Ticket Updated", group: "Project Management", icon: RefreshCw, integration: "jira", risk: "low", description: "Fires when a tracked Jira issue transitions or is edited.", reads: ["jira:issues"] },
  { key: "hook.github.pr", kind: "hook", label: "GitHub PR Opened", group: "Source Control", icon: GitPullRequest, integration: "github", risk: "low", description: "Fires when a pull request is opened against a tracked repo.", reads: ["github:pulls"] },
  { key: "hook.github.issue", kind: "hook", label: "GitHub Issue Created", group: "Source Control", icon: CircleDot, integration: "github", risk: "low", description: "Fires when a GitHub issue is created.", reads: ["github:issues"] },
  { key: "hook.slack.message", kind: "hook", label: "Slack Message Posted", group: "Communication", icon: MessageSquare, integration: "slack", risk: "low", description: "Fires when a message matching a filter lands in a watched channel.", reads: ["slack:messages"] },
  { key: "hook.ci.failed", kind: "hook", label: "CI Failed", group: "CI/CD", icon: XCircle, integration: "ci", risk: "low", description: "Fires when a CI pipeline run fails.", reads: ["ci:runs"] },
  { key: "hook.build.done", kind: "hook", label: "Build Completed", group: "CI/CD", icon: Hammer, integration: "ci", risk: "low", description: "Fires when a build finishes successfully.", reads: ["ci:runs"] },
  { key: "hook.deploy.done", kind: "hook", label: "Deployment Completed", group: "CI/CD", icon: Rocket, integration: "deploy", risk: "low", description: "Fires when a deployment reaches an environment.", reads: ["deploy:events"] },
  { key: "hook.calendar.focus", kind: "hook", label: "Calendar Focus Block Started", group: "Calendar", icon: CalendarClock, integration: "gcal", risk: "low", description: "Fires when a focus block begins on a connected calendar.", reads: ["calendar:events"] },
  { key: "hook.manual", kind: "hook", label: "Manual Run", group: "Manual", icon: MousePointerClick, integration: "hive", risk: "low", description: "Run the workflow on demand from Hive.", reads: [] },
  { key: "hook.schedule", kind: "hook", label: "Scheduled Run", group: "Schedule", icon: Clock, integration: "hive", risk: "low", description: "Run the workflow on a cron-style schedule.", reads: [] },
  { key: "hook.webhook", kind: "hook", label: "Webhook Received", group: "Webhook", icon: Webhook, integration: "webhook", risk: "low", description: "Fires on an inbound webhook with a shared secret.", reads: ["webhook:inbound"] },
  { key: "hook.github.merged", kind: "hook", label: "GitHub PR Merged", group: "Source Control", icon: GitMerge, integration: "github", risk: "low", description: "Fires when a pull request is merged into a tracked branch.", reads: ["github:pulls"] },
  { key: "hook.github.push", kind: "hook", label: "Commit Pushed", group: "Source Control", icon: GitBranch, integration: "github", risk: "low", description: "Fires on a push to a watched branch or path.", reads: ["github:commits"] },
  { key: "hook.github.release", kind: "hook", label: "Release Published", group: "Source Control", icon: Tag, integration: "github", risk: "low", description: "Fires when a release or tag is published.", reads: ["github:releases"] },
  { key: "hook.linear.created", kind: "hook", label: "Linear Issue Created", group: "Project Management", icon: CircleDot, integration: "linear", risk: "low", description: "Fires when a Linear issue is created in a tracked team.", reads: ["linear:issues"] },
  { key: "hook.email.received", kind: "hook", label: "Email Received", group: "Communication", icon: Mail, integration: "email", risk: "low", description: "Fires when a matching email lands in a connected inbox.", reads: ["email:inbox"] },
  { key: "hook.error.alert", kind: "hook", label: "Error Alert Triggered", group: "Monitoring", icon: AlertTriangle, integration: "monitor", risk: "low", description: "Fires when an error-rate alert trips in monitoring.", reads: ["monitor:alerts"] },
  { key: "hook.metric.threshold", kind: "hook", label: "Metric Threshold Crossed", group: "Monitoring", icon: LineChart, integration: "monitor", risk: "low", description: "Fires when a tracked metric crosses a configured threshold.", reads: ["monitor:metrics"] },
  { key: "hook.task.ready", kind: "hook", label: "Task Marked Ready", group: "Hive", icon: Inbox, integration: "hive", risk: "low", description: "Fires when a Hive task is refined and marked ready for ARES.", reads: ["hive:tasks"] },
  { key: "hook.workspace.created", kind: "hook", label: "Workspace Created", group: "Hive", icon: FolderGit2, integration: "workspace", risk: "low", description: "Fires when a new cloud dev workspace is provisioned.", reads: ["workspace:events"] },
  { key: "hook.agent.done", kind: "hook", label: "Agent Run Completed", group: "Hive", icon: Bot, integration: "ares", risk: "low", description: "Fires when an ARES agent run finishes (success or failure).", reads: ["ares:runs"] },
  { key: "hook.verification.failed", kind: "hook", label: "Verification Failed", group: "Hive", icon: ShieldAlert, integration: "verification", risk: "low", description: "Fires when a verification suite reports a failure.", reads: ["verification:results"] },
  { key: "hook.budget.threshold", kind: "hook", label: "Budget Threshold Reached", group: "Hive", icon: DollarSign, integration: "hive", risk: "low", description: "Fires when model/cloud spend crosses a budget line.", reads: ["hive:budget"] },
  { key: "hook.integration.degraded", kind: "hook", label: "Integration Degraded", group: "Hive", icon: XCircle, integration: "hive", risk: "low", description: "Fires when a connected integration reports degraded health.", reads: ["hive:integrations"] },

  /* PROCESSES (19) */
  { key: "proc.refine", kind: "process", label: "Refine Task", group: "Task Intelligence", icon: Wand2, integration: "hive", risk: "low", ai: true, description: "Clarify scope, acceptance criteria and edge cases for a task.", budget: true, reads: ["jira:issues"] },
  { key: "proc.followup", kind: "process", label: "Ask Follow-Up Questions", group: "Task Intelligence", icon: HelpCircle, integration: "slack", risk: "low", ai: true, description: "Generate and route clarifying questions to the reporter.", budget: true, external: true },
  { key: "proc.create-ws", kind: "process", label: "Create Workspace", group: "Workspace", icon: FolderGit2, integration: "workspace", risk: "low", description: "Provision an isolated cloud dev workspace for the task.", writes: ["workspace:create"] },
  { key: "proc.create-branch", kind: "process", label: "Create Branch", group: "Workspace", icon: GitBranch, integration: "github", risk: "low", description: "Cut a feature branch from the base branch.", code: true, writes: ["github:branches"] },
  { key: "proc.assign-ares", kind: "process", label: "Assign to ARES", group: "ARES", icon: ShieldCheck, integration: "ares", risk: "low", description: "Hand the prepared task to an ARES agent.", reads: ["ares:queue"] },
  { key: "proc.run-ares", kind: "process", label: "Run ARES Implementation", group: "ARES", icon: Bot, integration: "ares", risk: "medium", ai: true, description: "ARES implements the change with verification-first development.", budget: true, code: true, writes: ["github:commits"] },
  { key: "proc.verify", kind: "process", label: "Run Verification", group: "Verification", icon: BadgeCheck, integration: "verification", risk: "low", description: "Run the verification suite and collect proof of correctness.", budget: true, reads: ["repo:tests"] },
  { key: "proc.athena", kind: "process", label: "Run ATHENA API Readiness Check", group: "Athena", icon: Gauge, integration: "athena", risk: "low", ai: true, description: "Assess API readiness, drift and agent-asset coverage.", budget: true },
  { key: "proc.garlic", kind: "process", label: "Refresh Garlic Specialization", group: "Garlic", icon: Sparkles, integration: "garlic", risk: "medium", ai: true, description: "Retrain the repo's specialized agent profile.", budget: true },
  { key: "proc.byonc", kind: "process", label: "Route Through BYONC", group: "BYONC", icon: Network, integration: "byonc", risk: "low", description: "Route inference through the hybrid cost/latency optimizer.", budget: true },
  { key: "proc.analyze-logs", kind: "process", label: "Analyze Logs", group: "Task Intelligence", icon: FileSearch, integration: "hive", risk: "low", ai: true, description: "Parse failing logs and localize the probable root cause.", budget: true },
  { key: "proc.test-plan", kind: "process", label: "Generate Test Plan", group: "Verification", icon: ClipboardList, integration: "hive", risk: "low", ai: true, description: "Draft a verification & test plan from the change set.", budget: true },
  { key: "proc.release-notes", kind: "process", label: "Generate Release Notes", group: "Utilities", icon: ScrollText, integration: "hive", risk: "low", ai: true, description: "Summarize merged PRs into human-readable release notes.", budget: true },
  { key: "proc.risk", kind: "process", label: "Risk Classifier", group: "Security", icon: ShieldAlert, integration: "hive", risk: "low", ai: true, description: "Score the change for blast radius, security and rollback risk.", budget: true },
  { key: "proc.approval", kind: "process", label: "Human Approval Gate", group: "Human Review", icon: UserCheck, integration: "hive", risk: "medium", description: "Pause for a human to approve before continuing.", gate: true },
  { key: "proc.cost", kind: "process", label: "Cost Estimate", group: "Utilities", icon: DollarSign, integration: "hive", risk: "low", description: "Estimate model, cloud and verification cost for the run." },
  { key: "proc.security", kind: "process", label: "Security Review", group: "Security", icon: ShieldAlert, integration: "hive", risk: "medium", ai: true, description: "Run a security regression and dependency scan.", budget: true },
  { key: "proc.deps", kind: "process", label: "Dependency Impact Analysis", group: "Security", icon: GitCompare, integration: "hive", risk: "low", ai: true, description: "Map downstream impact of the change across services.", budget: true },
  { key: "proc.summarize", kind: "process", label: "Summarize Execution", group: "Utilities", icon: FileCheck2, integration: "hive", risk: "low", ai: true, description: "Produce a concise summary of the run for handoff.", budget: true },

  /* PUBLISHERS (14) */
  { key: "pub.jira.update", kind: "publish", label: "Update Jira Ticket", group: "Project Management", icon: Ticket, integration: "jira", risk: "low", description: "Transition status, add comments and link artifacts in Jira.", writes: ["jira:issues"] },
  { key: "pub.slack.post", kind: "publish", label: "Post Slack Message", group: "Communication", icon: MessageSquareText, integration: "slack", risk: "medium", description: "Post a message to a Slack channel.", external: true, writes: ["slack:messages"] },
  { key: "pub.github.pr", kind: "publish", label: "Create GitHub PR", group: "Source Control", icon: GitPullRequest, integration: "github", risk: "medium", description: "Open a pull request with the implemented change.", code: true, pr: true, writes: ["github:pulls"] },
  { key: "pub.github.comment", kind: "publish", label: "Add GitHub PR Comment", group: "Source Control", icon: MessageSquare, integration: "github", risk: "low", description: "Post a review comment on a pull request.", writes: ["github:comments"] },
  { key: "pub.email", kind: "publish", label: "Send Email", group: "Communication", icon: Mail, integration: "email", risk: "high", description: "Send an email — may reach external stakeholders.", external: true, writes: ["email:send"] },
  { key: "pub.confluence", kind: "publish", label: "Update Confluence", group: "Documentation", icon: BookOpen, integration: "confluence", risk: "low", description: "Create or update a Confluence page.", writes: ["confluence:pages"] },
  { key: "pub.notion", kind: "publish", label: "Create Notion Page", group: "Documentation", icon: StickyNote, integration: "notion", risk: "low", description: "Create a Notion page from generated content.", writes: ["notion:pages"] },
  { key: "pub.linear", kind: "publish", label: "Create Linear Update", group: "Project Management", icon: CircleDot, integration: "linear", risk: "low", description: "Post a project update to Linear.", writes: ["linear:updates"] },
  { key: "pub.release", kind: "publish", label: "Publish Release Notes", group: "Documentation", icon: ScrollText, integration: "hive", risk: "medium", description: "Publish release notes to the changelog surface.", external: true },
  { key: "pub.deploy", kind: "publish", label: "Trigger Deployment", group: "Deployment", icon: Rocket, integration: "deploy", risk: "restricted", description: "Trigger a deployment to a target environment.", deploy: true, writes: ["deploy:trigger"] },
  { key: "pub.calendar", kind: "publish", label: "Create Calendar Block", group: "Calendar", icon: CalendarClock, integration: "gcal", risk: "low", description: "Schedule a calendar block for follow-up work.", writes: ["calendar:events"] },
  { key: "pub.webhook", kind: "publish", label: "Emit Webhook", group: "Webhook", icon: Webhook, integration: "webhook", risk: "medium", description: "Emit an outbound webhook to another system.", external: true, writes: ["webhook:outbound"] },
  { key: "pub.artifact", kind: "publish", label: "Save Artifact", group: "Artifact", icon: Save, integration: "artifact", risk: "low", description: "Persist a generated artifact to the artifact store.", writes: ["artifact:store"] },
  { key: "pub.notify", kind: "publish", label: "Notify Assignee", group: "Communication", icon: Bell, integration: "slack", risk: "low", description: "Notify the task assignee across their preferred channel.", external: true },
];

export const NODE_DEF_MAP: Record<string, WfNodeDef> = Object.fromEntries(
  NODE_DEFS.map((d) => [d.key, d])
);

export function nodeDef(key: string): WfNodeDef {
  return NODE_DEF_MAP[key] ?? NODE_DEFS[0];
}

/* Palette: kind → ordered groups → defs. */
export const PALETTE: Record<WfNodeKind, { group: string; defs: WfNodeDef[] }[]> =
  (() => {
    const out = { hook: [], process: [], publish: [] } as Record<
      WfNodeKind,
      { group: string; defs: WfNodeDef[] }[]
    >;
    (["hook", "process", "publish"] as WfNodeKind[]).forEach((kind) => {
      const groups: string[] = [];
      const byGroup = new Map<string, WfNodeDef[]>();
      NODE_DEFS.filter((d) => d.kind === kind).forEach((d) => {
        if (!byGroup.has(d.group)) {
          byGroup.set(d.group, []);
          groups.push(d.group);
        }
        byGroup.get(d.group)!.push(d);
      });
      out[kind] = groups.map((g) => ({ group: g, defs: byGroup.get(g)! }));
    });
    return out;
  })();

/* ── Canvas layout ────────────────────────────────────────────────────────── */

export const NODE_W = 248;
export const NODE_H = 92;
export const LANE_X: Record<WfNodeKind, number> = {
  hook: 56,
  process: 470,
  publish: 1180,
};
export const LANE_LABEL: Record<WfNodeKind, string> = {
  hook: "Hooks",
  process: "Processes",
  publish: "Publishers",
};
const V_GAP = 128;
const Y_TOP = 120;

/** Default placement for a freshly-added node of a given kind. */
export function defaultPlacement(kind: WfNodeKind, count: number) {
  return { x: LANE_X[kind], y: Y_TOP + count * V_GAP };
}

function defaultConfig(def: WfNodeDef): Record<string, string | number | boolean> {
  const c: Record<string, string | number | boolean> = {};
  if (def.kind === "hook") {
    if (def.integration === "jira") c.project = "PAY";
    if (def.integration === "github") c.repo = "checkout-service";
    if (def.integration === "slack") c.channel = "#eng-alerts";
    if (def.key === "hook.schedule") c.frequency = "every 6h";
  }
  if (def.ai) c.inference = "BYONC · cost-optimized";
  if (def.gate) {
    c.approver = "tech-lead";
    c.timeout = "4h";
    c.onTimeout = "escalate";
  }
  return c;
}

/** Build a clean lane-laid-out graph from ordered def keys. */
export function buildGraph(
  hookKeys: string[],
  processKeys: string[],
  publishKeys: string[]
): { nodes: WfNode[]; edges: WfEdge[] } {
  const nodes: WfNode[] = [];
  const make = (key: string, i: number, kind: WfNodeKind) => {
    const def = nodeDef(key);
    const { x, y } = defaultPlacement(kind, i);
    const node: WfNode = {
      id: uid("n"),
      defKey: key,
      x,
      y,
      title: def.label,
      approval: def.gate,
      config: defaultConfig(def),
      configComplete: 1,
    };
    nodes.push(node);
    return node;
  };

  const hooks = hookKeys.map((k, i) => make(k, i, "hook"));
  const procs = processKeys.map((k, i) => make(k, i, "process"));
  const pubs = publishKeys.map((k, i) => make(k, i, "publish"));

  const edges: WfEdge[] = [];
  const chain = [...hooks, ...procs, ...pubs];
  for (let i = 0; i < chain.length - 1; i++) {
    const from = chain[i];
    const to = chain[i + 1];
    const def = nodeDef(from.defKey);
    let label: string | undefined;
    let tone: WfEdgeTone = "default";
    if (def.key === "proc.verify") {
      label = "if passes";
      tone = "success";
    } else if (def.gate) {
      label = "on approve";
      tone = "approval";
    } else if (def.key === "proc.refine") {
      label = "if ready";
    } else if (def.key === "proc.risk") {
      label = "if low risk";
    }
    edges.push({ id: uid("e"), source: from.id, target: to.id, label, tone });
  }
  return { nodes, edges };
}
